import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';
import { avatarHue, initials } from '../lib/format';

/**
 * The component primitives.
 *
 * Built on Radix behaviour where interaction is involved, so focus management,
 * keyboard navigation and ARIA are correct by construction rather than by
 * audit. Everything here is sized for density: an agent looks at these
 * components for eight hours, and the difference between a 32px and a 40px row
 * is several hundred fewer pixels of scrolling per screen.
 */

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white hover:bg-accent-hover shadow-sm disabled:bg-accent/50',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-sunken shadow-sm',
  ghost: 'text-ink-muted hover:bg-sunken hover:text-ink',
  subtle: 'bg-sunken text-ink hover:bg-line',
  danger: 'bg-critical text-white hover:brightness-95 shadow-sm',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // For the dense controls in a side panel header, where a `sm` button is tall
  // enough to push the panel title off its own baseline.
  xs: 'h-6 px-1.5 text-micro gap-1',
  sm: 'h-7 px-2.5 text-mini gap-1.5',
  md: 'h-8 px-3 text-body gap-1.5',
  lg: 'h-10 px-4 text-sm gap-2',
  icon: 'h-8 w-8 justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Render as the child element, for links that should look like buttons. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading, asChild, children, disabled, ...props },
  ref,
) {
  const classes = cn(
    'inline-flex items-center rounded-control font-medium whitespace-nowrap select-none',
    'transition-[background-color,border-color,color,box-shadow] duration-100',
    'disabled:pointer-events-none disabled:opacity-60',
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    className,
  );

  /**
   * `asChild` merges these props onto the caller's element — a router `Link`,
   * usually — so the styling can be reused without nesting an anchor inside a
   * button. Radix's `Slot` requires exactly one child, so the spinner is not
   * rendered in this mode; a link that navigates has nothing to wait for.
   */
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      /**
       * A button inside a form is a submit button unless it says otherwise, and
       * that default is wrong for almost every button in this app. It made the
       * whole conditions panel in the automation editor submit the rule: adding
       * a condition, removing one, adding a group — each saved the half-built
       * rule instead of editing it. Buttons that do submit say so explicitly,
       * and every form here has exactly one.
       */
      type="button"
      // A loading button stays focusable but rejects activation, so a screen
      // reader does not lose its place mid-interaction.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {loading && <Loader2 aria-hidden className="size-3.5 shrink-0 animate-spin" />}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

const FIELD_BASE =
  'w-full rounded-control border bg-surface text-ink placeholder:text-ink-subtle ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
  'aria-[invalid=true]:border-critical aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-critical';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        /**
         * Follows whatever is typed into it. An agent searching for a ticket
         * in Arabic, or filling in an Arabic catalog field, should see the
         * caret and the text where they belong rather than fighting a
         * left-to-right box. Declared before the spread so a caller that
         * genuinely needs a fixed direction can still say so.
         */
        dir="auto"
        className={cn(FIELD_BASE, 'h-8 border-line-strong px-2.5 text-body', className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        dir="auto"
        className={cn(FIELD_BASE, 'min-h-20 border-line-strong px-2.5 py-2 text-body', className)}
        {...props}
      />
    );
  },
);

/**
 * A native select, styled once.
 *
 * Native rather than a custom listbox on purpose: it is keyboard-complete,
 * type-to-select works, and on a phone it opens the platform picker. Before
 * this existed there were eleven different hand-styled selects across the
 * screens, at three heights and two border weights.
 */
export const Select = forwardRef<
  HTMLSelectElement,
  // The native `size` attribute (visible row count) is never wanted here, and
  // leaving it in would make this `size` an impossible number-and-string type.
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & { size?: 'sm' | 'md' }
>(function Select({ className, size = 'md', children, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(
        FIELD_BASE,
        'border-line-strong pe-7 ps-2.5',
        size === 'sm' ? 'h-7 text-mini' : 'h-8 text-body',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
});

/**
 * An on/off control for a setting that takes effect as a whole — "notify on
 * breach", "allow attachments". A checkbox is still right for choosing items
 * from a list; this is for flipping one thing.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
  id,
  className,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** The accessible name, when no visible `<label htmlFor>` points here. */
  label?: string;
  id?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-accent' : 'bg-line-strong',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute top-0.5 size-4 rounded-full bg-white shadow-sm transition-[inset-inline-start]',
          checked ? 'start-4.5' : 'start-0.5',
        )}
      />
    </button>
  );
}

export interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * A labelled form field.
 *
 * The error is wired to the control through `aria-describedby` by the caller;
 * showing it visually without announcing it is the most common accessibility
 * failure in forms.
 */
export function Field({ label, htmlFor, error, hint, required, children, className }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-mini font-medium text-ink-muted">
        {label}
        {required && (
          <span className="ms-0.5 text-critical" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-mini text-ink-subtle">{hint}</p>}
      {error && (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} role="alert" className="text-mini text-critical-ink">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

type BadgeTone = 'neutral' | 'accent' | 'positive' | 'caution' | 'critical' | 'informative';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-sunken text-ink-muted border-line',
  accent: 'bg-accent-soft text-accent-ink border-accent-line',
  positive: 'bg-positive-soft text-positive-ink border-positive-line',
  caution: 'bg-caution-soft text-caution-ink border-caution-line',
  critical: 'bg-critical-soft text-critical-ink border-critical-line',
  informative: 'bg-informative-soft text-informative-ink border-informative-line',
};

export function Badge({
  tone = 'neutral',
  className,
  dot,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone; dot?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5',
        'text-mini leading-none font-medium whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
      {...props}
    >
      {/* A dot alongside the label so state is not carried by colour alone. */}
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current opacity-70" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

export function Avatar({
  name,
  size = 'md',
  className,
}: {
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const hue = avatarHue(name);
  const sizes = {
    xs: 'size-5 text-[0.6rem]',
    sm: 'size-6 text-micro',
    md: 'size-7 text-mini',
    lg: 'size-10 text-sm',
  };

  return (
    <span
      // The name is already rendered beside the avatar wherever it appears, so
      // the initials are decorative and must not be announced twice.
      aria-hidden
      title={name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none',
        sizes[size],
        className,
      )}
      style={{
        backgroundColor: `oklch(0.92 0.05 ${hue})`,
        color: `oklch(0.38 0.12 ${hue})`,
      }}
    >
      {initials(name)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Layout and feedback
// ---------------------------------------------------------------------------

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-card border border-line bg-surface shadow-raised', className)}
      {...props}
    />
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-ink-subtle', className)}>
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span className="sr-only-focusable">{label}</span>
    </span>
  );
}

/**
 * A skeleton placeholder.
 *
 * Preferred over a spinner for content whose shape is known: it keeps layout
 * stable, so nothing jumps when the data arrives.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded bg-sunken', className)}
    />
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && <div className="mb-3 text-ink-subtle">{icon}</div>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-mini text-ink-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  requestId,
  onRetry,
}: {
  title?: string;
  message: string;
  requestId?: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <p className="text-sm font-medium text-critical-ink">{title}</p>
      <p className="mt-1 max-w-md text-mini text-ink-muted">{message}</p>
      {/* Surfacing the request id turns "it broke" into something support can
          actually trace in the logs. */}
      {requestId && (
        <p className="mt-2 font-mono text-micro text-ink-subtle">Reference: {requestId}</p>
      )}
      {onRetry && (
        <Button className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** A row of labelled facts, used in ticket properties and detail panels. */
export function DefinitionRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] items-start gap-2 py-1.5">
      <dt className="pt-0.5 text-mini text-ink-subtle">{label}</dt>
      <dd className="min-w-0 text-mini text-ink">{children}</dd>
    </div>
  );
}

export function Separator({ className }: { className?: string }) {
  return <div role="separator" className={cn('h-px w-full bg-line', className)} />;
}
